/**
 * Server-time correction for offline sync.
 *
 * Why: a factory kiosk whose system clock drifts 5 minutes ahead would stamp
 * its `optimistic_updated_at` with a future time. The conflict check in
 * [sync/engine.ts](./engine.ts) compares `data.updated_at > op.optimistic_updated_at`
 * as strings — a forward clock skew makes the local timestamp look newer than
 * real server data, so conflicts are silently missed and last-write-wins gets
 * applied to stale data.
 *
 * Fix: on every sign-in, decode the Supabase access-token JWT and read its `iat`
 * (issued-at, seconds since epoch, set by the auth server). The offset from
 * local clock is cached in module state. All timestamp writes go through
 * `nowServer()` which applies the offset. Cost: one JWT decode per session,
 * zero network calls.
 *
 * Precision: JWTs stamp `iat` in whole seconds, so the offset is accurate to
 * ~1s — plenty for conflict detection (server `updated_at` is µs-precision
 * but collisions require skew on the order of minutes).
 */

let serverOffsetMs = 0;

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return null;
    // Normalise URL-safe base64 to standard base64
    const b64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Computes and caches the server-vs-local clock offset from a Supabase
 * access-token JWT. Call this on sign-in and whenever a fresh session is
 * issued.
 */
export function setServerTimeFromJWT(accessToken: string | null | undefined): void {
  if (!accessToken) return;
  const payload = decodeJwtPayload(accessToken);
  const iat = payload?.iat;
  if (typeof iat !== 'number') return;
  serverOffsetMs = iat * 1000 - Date.now();
  if (import.meta.env.DEV && Math.abs(serverOffsetMs) > 5000) {
    console.info(`[serverTime] clock skew detected: ${serverOffsetMs}ms`);
  }
}

/**
 * Returns an ISO-8601 timestamp corrected to server time. Use this in place
 * of `new Date().toISOString()` for every value that will be compared against
 * a server-issued timestamp (optimistic_updated_at, conflict checks).
 */
export function nowServer(): string {
  return new Date(Date.now() + serverOffsetMs).toISOString();
}

/** Exposed for tests. */
export function _getOffsetMs(): number {
  return serverOffsetMs;
}

/** Exposed for tests. */
export function _resetOffsetForTests(): void {
  serverOffsetMs = 0;
}
